import React from "react";
import { PlateView } from "./PlateView"; // Assumes PlateView.jsx is in the same directory

const PlateViewItem = ({ vehicle, isSelected, onToggle, isAllSelected }) => {
    /**
     * Calculates and formats the display text and style for the last log time.
     */
    const getLogStatus = lastLogTimestamp => {
        if (!lastLogTimestamp) {
            return { text: "Unknown", style: { color: "#757575", backgroundColor: "#F5F5F5" } };
        }
        const now = new Date();
        const lastLogDate = new Date(lastLogTimestamp);
        const diffInMinutes = Math.round((now - lastLogDate) / (1000 * 60));

        if (diffInMinutes < 3) {
            return { text: "Live", style: { color: "#2E7D32", backgroundColor: "#E8F5E9" } };
        }
        if (diffInMinutes < 180) {
            const hours = Math.floor(diffInMinutes / 60);
            const text = hours > 0 ? `${hours}h ago` : `${diffInMinutes}m ago`;
            return { text, style: { color: "#F9A825", backgroundColor: "#FFFDE7" } };
        }
        const hours = Math.floor(diffInMinutes / 60);
        const days = Math.floor(hours / 24);
        const text = days > 0 ? `${days}d ago` : `${hours}h ago`;
        return { text, style: { color: "#C62828", backgroundColor: "#FFEBEE" } };
    };

    /**
     * Formats the distance prop for user-friendly display.
     */
    const formatDistance = distance => {
        if (typeof distance === "string") return distance; // Handles "Location needed"
        if (distance === null || typeof distance === "undefined") return "... km"; // Loading state
        if (distance < 1) return `${Math.round(distance * 1000)}m away`; // Use meters if close
        return `${distance.toFixed(1)}km away`; // Use kilometers
    };

    const { text: logText, style: logStyle } = getLogStatus(vehicle.lastLog);

    const stylesOld = {
        container: { margin: "4px", cursor: "pointer", borderRadius: "8px", transition: "background-color 0.2s ease-in-out" },
        card: { display: "flex", flexDirection: "row", padding: "10px", backgroundColor: isSelected ? "#FEF3C7" : "#FFFFFF", border: isSelected ? "1px solid #804600" : "0px solid #E0E0E0", borderRadius: "4px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }, //804600
        leftColumn: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start" },
        rightColumn: { flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between" },
        vehicleName: { padding: "4px", opacity: 0.8, color: "#333333", fontSize: "12px", fontWeight: "900" },
        plateContainer: { marginTop: "4px", marginBottom: "0px", padding: "4px" },
        logStatus: { padding: "0px 8px", fontSize: "12px", fontWeight: "bold", borderRadius: "12px", textAlign: "center" },
        distance: { padding: "4px", opacity: 0.7, color: "#333333", fontSize: "14px" },
    };

    const isActive = isAllSelected || isSelected;

    const styles = {
        container: {
            padding: "12px 16px",
            marginTop: "24px",
            cursor: "pointer",
            backgroundColor: "transparent",
            transition: "background-color 0.15s ease",
        },
        itemWrapper: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "12px",
        },
        infoSection: {
            flex: 1,
            display: "flex",
            flexDirection: "row",
            opacity: isActive ? 1 : 0.35,
            transition: "opacity 0.2s ease",
        },
        leftColumn: { flex: 1, display: "flex", flexDirection: "column" },
        rightColumn: { display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center" },
        vehicleName: { color: "#64748b", fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", marginLeft: "8px" },
        plateContainer: { margin: "0" },
        logStatus: { padding: "2px 8px", fontSize: "10px", fontWeight: "800", borderRadius: "4px" },
        distance: { color: "#1e293b", fontSize: "13px", fontWeight: "700", marginTop: "4px" },
        toggleWrapper: { display: "flex", alignItems: "center" },
        switch: {
            width: "36px",
            height: "20px",
            borderRadius: "12px",
            backgroundColor: isActive ? "#2563eb" : "#94a3b8",
            position: "relative",
            transition: "all 0.2s ease",
        },
        knob: {
            width: "14px",
            height: "14px",
            backgroundColor: "#ffffff",
            borderRadius: "50%",
            position: "absolute",
            top: "3px",
            left: isActive ? "19px" : "3px",
            transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        viewAllText: {
            fontSize: "14px",
            fontWeight: "700",
            color: isAllSelected ? "#1e293b" : "#64748b",
            flex: 1,
        },
    };

    // JSX for the "View All" toggle (SelectAllViewItem replacement)
    // <div style={styles.container} onClick={() => onToggle("all")}>
    //     <div style={styles.itemWrapper}>
    //         <div style={styles.viewAllText}>View All</div>
    //         <div style={styles.toggleWrapper}>
    //             <div style={styles.switch}><div style={styles.knob} /></div>
    //         </div>
    //     </div>
    // </div>

    return (
        <div style={styles.container} onClick={() => onToggle && onToggle(vehicle.id)}>
            <div style={styles.itemWrapper}>
                <div style={styles.infoSection}>
                    <div style={styles.leftColumn}>
                        <div style={styles.vehicleName}>{`${vehicle.tag ?? ""} ${vehicle.brand ?? ""}`.trim() || "Unknown"}</div>
                        <div style={styles.plateContainer}>
                            <PlateView plate={vehicle.plate} />
                        </div>
                    </div>
                    <div style={styles.rightColumn}>
                        <div style={{ ...styles.logStatus, ...logStyle }}>{logText}</div>
                        <div style={styles.distance}>{formatDistance(vehicle.distance)}</div>
                    </div>
                </div>
                {onToggle && (
                    <div style={styles.toggleWrapper}>
                        <div style={styles.switch}>
                            <div style={styles.knob} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
    // return (
    //     <div style={styles.container} onClick={() => onToggle(vehicle.id)}>
    //         <div style={styles.card}>
    //             <div style={styles.leftColumn}>
    //                 <div style={styles.vehicleName}>{`${vehicle.tag ?? ""} ${vehicle.brand ?? ""} ${vehicle.model ?? ""}`.trim() || "Unknown"}</div>
    //                 <div style={styles.plateContainer}>
    //                     <PlateView plate={vehicle.plate} />
    //                 </div>
    //             </div>
    //             <div style={styles.rightColumn}>
    //                 <div style={{ ...styles.logStatus, ...logStyle }}>{logText}</div>
    //                 <div style={styles.distance}>{formatDistance(vehicle.distance)}</div>
    //             </div>
    //         </div>
    //     </div>
    // );
};

export { PlateViewItem };
